import { api } from "@/lib/api";
import { logError, logInfo } from "@/lib/monitoring";

// Check if we're in development mode
const isDev = import.meta.env.MODE === "development";

// Real Stripe Plan Configuration
// Prices are dynamically created in Stripe based on these configurations
export const STRIPE_PLANS = {
  // Free trial - existing users only
  trial: {
    id: "plan_WaVCubs2Zn9x0",
    name: "Free Trial",
    price: 0,
    emailQuota: 15000,
    overage: 15, // $15 per 10k emails
    features: [
      "30 composer uses",
      "unlimited deployed leads",
      "10 automated follow ups",
      "all core features",
    ],
    description: "Perfect for trying out the platform",
  },

  // Regular pricing tiers
  usage: {
    id: null, // Free plan - no Stripe subscription needed
    name: "Usage Plan",
    price: 0,
    emailQuota: 5000,
    overage: 15, // $15 per 10k emails
    features: [
      "30 composer uses",
      "unlimited deployed leads",
      "10 automated follow ups",
      "all core features",
    ],
    description: "Free pay-as-you-go plan",
  },

  // Autonomous AI plans - NEW
  autonomous_ai: {
    id: "autonomous_ai", // Dynamic pricing plan
    name: "Autonomous AI Marketer",
    price: 97,
    emailQuota: 150000,
    overage: 8, // $8 per 10k emails
    features: [
      "150K emails/month included",
      "AI configures servers & domains",
      "Finds prospects from 250M+ database",
      "Writes & sends personalized emails",
      "Responds to replies automatically",
      "Self-optimizes campaigns 24/7",
      "Tracks conversions with pixels"
    ],
    description: "Complete autonomous AI marketing system",
  },

  pro_ai: {
    id: "pro_ai", // Dynamic pricing plan  
    name: "Pro AI Marketer",
    price: 297,
    emailQuota: 500000,
    overage: 8, // $8 per 10k emails
    features: [
      "500K emails/month included",
      "Everything in Growth plan",
      "Priority AI learning",
      "Advanced analytics",
      "Dedicated support",
      "White-label options"
    ],
    description: "Most popular autonomous AI choice",
  },

  starter: {
    id: "plan_ioGdU6Sz02H4W",
    name: "Starter",
    price: 9,
    emailQuota: 15000,
    overage: 10, // $10 per 10k emails
    features: [
      "250 composer uses",
      "250 automated follow ups",
      "access to new features",
      "all unlimited features",
    ],
    description: "Great for small businesses",
  },

  basic: {
    id: "plan_fsQCiaUcs4ftY",
    name: "Basic",
    price: 20,
    emailQuota: 40000,
    overage: 10, // $10 per 10k emails
    features: [
      "250 composer uses",
      "250 automated follow ups",
      "access to new features",
      "all unlimited features",
    ],
    description: "Perfect for growing teams",
  },

  standard: {
    id: "plan_RKjddH9EYYRhp",
    name: "Standard",
    price: 50,
    emailQuota: 100000,
    overage: 10, // $10 per 10k emails
    features: [
      "250 composer uses",
      "250 automated follow ups",
      "access to new features",
      "all unlimited features",
    ],
    description: "Most popular plan",
  },

  pro: {
    id: "plan_wiqNK0rVwVRDj",
    name: "Pro",
    price: 80,
    emailQuota: 200000,
    overage: 10, // $10 per 10k emails
    features: [
      "250 composer uses",
      "250 automated follow ups",
      "access to new features",
      "all unlimited features",
    ],
    description: "For serious marketers",
  },

  junior_pro: {
    id: "plan_6tr4DQ25RWB76",
    name: "Junior Pro",
    price: 180, // Doubled from $90 to $180
    emailQuota: 150000, // Custom quota for this plan
    overage: 10, // $10 per 10k emails
    features: [
      "250 composer uses",
      "250 automated follow ups",
      "access to new features",
      "all unlimited features",
    ],
    description: "Professional features for growing businesses",
  },

  enterprise: {
    id: "plan_6tr4DQ25RWB76",
    name: "Enterprise",
    price: 199,
    emailQuota: 500000,
    overage: 10, // $10 per 10k emails
    features: [
      "250 composer uses",
      "250 automated follow ups",
      "access to new features",
      "all unlimited features",
    ],
    description: "For large organizations",
  },

  scale: {
    id: "plan_7yqnjALUUjQw0",
    name: "Scale",
    price: 499,
    emailQuota: 1500000,
    overage: 10, // $10 per 10k emails
    features: [
      "250 composer uses",
      "250 automated follow ups",
      "access to new features",
      "all unlimited features",
    ],
    description: "Massive scale operations",
  },

  growth: {
    id: "plan_h42fbNOJRnXGB",
    name: "Growth",
    price: 97,
    emailQuota: 150000,
    overage: 10, // $10 per 10k emails
    features: [
      "150,000 emails/month",
      "AI writes & sends emails",
      "Auto-responds to replies",
      "Meeting booking automation",
      "3-day free trial",
    ],
    description: "AI-powered outreach at scale",
  },

  moon_scale: {
    id: "plan_0IyfLdf8M03Cq",
    name: "MOON SCALE",
    price: 2400, // Doubled from $1200 to $2400
    emailQuota: 2000000, // Custom quota for this plan
    overage: 10, // $10 per 10k emails
    features: [
      "250 composer uses",
      "250 automated follow ups",
      "access to new features",
      "all unlimited features",
    ],
    description: "For serious marketers scaling to the moon",
  },

  professional: {
    id: "plan_IGZm9jujbeIDk",
    name: "Professional",
    price: 1999,
    emailQuota: 7500000,
    overage: 10, // $10 per 10k emails
    features: [
      "250 composer uses",
      "250 automated follow ups",
      "access to new features",
      "all unlimited features",
    ],
    description: "Professional-grade volume",
  },

  elite: {
    id: "plan_0IyfLdf8M03Cq",
    name: "Elite",
    price: 2999,
    emailQuota: 15000000,
    overage: 10, // $10 per 10k emails
    features: [
      "250 composer uses",
      "250 automated follow ups",
      "access to new features",
      "all unlimited features",
    ],
    description: "Elite-level performance",
  },

  ultimate: {
    id: "plan_CNi9ifROPSSvf",
    name: "Ultimate",
    price: 5000,
    emailQuota: 30000000,
    overage: 10, // $10 per 10k emails
    features: [
      "250 composer uses",
      "250 automated follow ups",
      "access to new features",
      "all unlimited features",
    ],
    description: "Ultimate scale and performance",
  },
};

// Main plan types for billing page
export const MAIN_PLANS = ["usage", "growth", "pro_ai", "moon_scale"];

// Auto-billing plans that should trigger automatic charges
export const AUTO_BILLING_PLANS = ["usage"];

/**
 * Service for handling Stripe.com integrations
 * Includes plan management, subscription handling, and billing functions
 */
class StripeService {
  /**
   * Get all available plans
   */
  getPlans() {
    return STRIPE_PLANS;
  }

  /**
   * Get main plans for billing page
   */
  getMainPlans() {
    return MAIN_PLANS.map((key) => ({
      key,
      ...STRIPE_PLANS[key],
    }));
  }

  /**
   * Get plan by key
   */
  getPlan(planKey: string) {
    return STRIPE_PLANS[planKey] || null;
  }

  /**
   * Create custom plan for high-volume users
   * @param emailVolume Monthly email volume
   * @param userEmail User's email for plan creation
   * @returns Plan details with Stripe plan ID
   */
  async createCustomPlan(emailVolume: number, userEmail: string) {
    try {
      console.log("🎯 Creating custom plan for:", emailVolume, "emails");

      const customPlan = this.calculateCustomPlan(emailVolume);

      // For custom plans, we'll pass the pricing details directly to checkout
      // instead of creating a plan in Stripe first
      return {
        ...customPlan,
        id: `custom_${emailVolume}`,
        stripePlanId: null, // Will be created dynamically during checkout
        emailVolume: emailVolume,
      };
    } catch (error) {
      console.error("❌ Error creating custom plan:", error);
      logError("Error creating custom plan", { error, emailVolume, userEmail });
      throw error;
    }
  }

  /**
   * Initiate Stripe checkout for a specific plan
   * @param email User's email
   * @param planKey Plan key from STRIPE_PLANS
   * @param password User's password (for new users)
   * @param name User's name (for new users)
   * @param customEmailVolume Optional custom email volume for custom plans
   */
  async initiateCheckout(
    email: string,
    planKey: string,
    password?: string,
    name?: string,
    customEmailVolume?: number
  ): Promise<{ checkoutUrl: string }> {
    try {
      console.log("🚀 Stripe Service: Initiating checkout for plan:", planKey);

      // If this is the free usage plan, return to dashboard
      if (planKey === "usage") {
        console.log("✅ Free usage plan selected, redirecting to dashboard");
        return {
          checkoutUrl: "/overview",
        };
      }

      // Handle custom plans
      if (planKey === "custom" && customEmailVolume) {
        console.log("🎯 Creating custom plan for volume:", customEmailVolume);
        const customPlan = await this.createCustomPlan(
          customEmailVolume,
          email
        );

        // Pass custom plan details directly to checkout
        const response = await api.post("/auth/initiate-stripe-checkout", {
          email,
          planId: "custom",
          planName: customPlan.name,
          planPrice: customPlan.price,
          planInterval: "month",
          password,
          name,
          isCustomPlan: true,
          emailVolume: customEmailVolume,
        });

        return response.data;
      }

      const plan = STRIPE_PLANS[planKey];
      if (!plan) {
        throw new Error(`Invalid plan key: ${planKey}`);
      }

      // Handle plans without Stripe IDs (like usage plan)
      if (!plan.id || plan.price === 0) {
        console.log(`✅ Plan ${planKey} does not require Stripe checkout`);
        
        // For new users signing up with free plan, still create the user
        if (password && name) {
          const response = await api.post("/auth/initiate-stripe-checkout", {
            email,
            planId: "usage",
            planName: plan.name,
            planPrice: 0,
            planInterval: "month",
            password,
            name,
          });
          return response.data;
        }
        
        return {
          checkoutUrl: "/overview",
        };
      }

      if (isDev) {
        // In development, simulate checkout
        console.log(
          `[DEV] Would initiate Stripe checkout for plan ${plan.name} (${plan.id})`
        );

        // Return mock checkout URL
        return {
          checkoutUrl: `https://checkout.stripe.com/${plan.id}?d2c=true&dev=true`,
        };
      }

      // For existing users upgrading/changing plans
      if (!password || !name) {
        const response = await api.post("/auth/initiate-plan-change", {
          email,
          planId: plan.id, // Send original plan ID
          planName: plan.name,
          planPrice: plan.price,
          planInterval: "month", // All your plans are monthly
        });

        return response.data;
      }

      // For new users signing up
      const response = await api.post("/auth/initiate-stripe-checkout", {
        email,
        planId: plan.id, // Send original plan ID
        planName: plan.name,
        planPrice: plan.price,
        planInterval: "month", // All your plans are monthly
        password,
        name,
      });

      console.log("✅ Stripe Service: Checkout URL generated successfully");
      
      // Feature flag: Use checkout confirmation page
      const useConfirmationPage = localStorage.getItem('use_checkout_confirmation') === 'true';
      
      if (useConfirmationPage && response.data.checkoutUrl) {
        // Get plan details for the confirmation page
        const planDetails = STRIPE_PLANS[planKey];
        const confirmUrl = `/#/checkout-confirm?plan=${encodeURIComponent(plan.name)}&price=${plan.price}&emails=${plan.emailQuota}&checkoutUrl=${encodeURIComponent(response.data.checkoutUrl)}`;
        return { checkoutUrl: confirmUrl };
      }
      
      return response.data;
    } catch (error) {
      console.error("❌ Stripe Service: Error initiating checkout:", error);
      logError("Error initiating Stripe checkout", { error, email, planKey });

      // Provide more specific error messages
      if (error.message?.includes("Invalid plan")) {
        throw new Error(
          `The selected plan "${planKey}" is not available. Please choose a different plan.`
        );
      } else if (error.response?.status === 404) {
        throw new Error(
          "Payment service is temporarily unavailable. Please try again later."
        );
      } else if (error.response?.status >= 500) {
        throw new Error(
          "Payment processing error. Please contact support if this persists."
        );
      }

      throw error;
    }
  }

  /**
   * Get user's current subscription details
   * @param userId User ID
   */
  async getSubscription(userId: string) {
    try {
      if (isDev) {
        // In development, return mock subscription
        const mockSub = {
          plan: "usage",
          status: "active",
          emailQuota: 5000,
          emailsUsed: 1250,
          renewalDate: new Date(
            Date.now() + 30 * 24 * 60 * 60 * 1000
          ).toISOString(),
          price: 0,
        };

        console.log("[DEV] Mock subscription:", mockSub);
        return mockSub;
      }

      // Use the correct billing status endpoint from the API gateway
      const response = await api.get(`/billing/subscription/status`);
      return response.data;
    } catch (error) {
      console.error("Error getting subscription:", error);
      logError("Error getting subscription", { error, userId });
      return null;
    }
  }

  /**
   * Get user's usage history and billing records
   * @param userId User ID
   */
  async getUsageHistory(userId: string) {
    try {
      if (isDev) {
        // In development, return mock usage history
        const mockHistory = [
          {
            date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
            type: "email_overage",
            quantity: 10000,
            amount: 1000, // $10.00 in cents
            description: "Additional 10,000 emails",
          },
          {
            date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
            type: "subscription",
            quantity: 1,
            amount: 18000, // $180.00 in cents
            description: "Junior Pro monthly subscription",
          },
        ];

        console.log("[DEV] Mock usage history:", mockHistory);
        return mockHistory;
      }

      const response = await api.get(`/billing/usage-history/${userId}`);
      return response.data;
    } catch (error) {
      console.error("Error getting usage history:", error);
      logError("Error getting usage history", { error, userId });
      return [];
    }
  }

  /**
   * Track email usage and automatically handle overage billing
   * @param userId User ID to track usage for
   * @param emailCount Number of emails to add to usage
   * @param autoCharge Whether to automatically charge for overages
   */
  async trackEmailUsage(
    userId: string,
    emailCount: number,
    autoCharge: boolean = true
  ): Promise<{
    needsCharging: boolean;
    currentCount: number;
    charged?: boolean;
  }> {
    try {
      console.log(
        `📧 Stripe Service: Tracking ${emailCount} emails for user ${userId}`
      );

      if (isDev) {
        // In development, return mock tracking
        const mockUsage = {
          emailsUsed: 12500,
          emailQuota: 5000,
          overage: 7500,
        };

        const needsCharging = mockUsage.overage > 0 && autoCharge;
        console.log(
          `[DEV] Mock usage tracking - Used: ${mockUsage.emailsUsed}, Quota: ${mockUsage.emailQuota}, Overage: ${mockUsage.overage}`
        );

        return {
          needsCharging,
          currentCount: mockUsage.emailsUsed,
          charged: false,
        };
      }

      // Get current subscription to check if auto-charging is enabled
      const subscription = await this.getSubscription(userId);
      const planKey = subscription?.plan || "usage";
      const plan = STRIPE_PLANS[planKey];

      if (!plan) {
        throw new Error(`Invalid plan: ${planKey}`);
      }

      // Check if this is an auto-billing plan
      const isAutoBillingPlan = AUTO_BILLING_PLANS.includes(planKey);

      // Track usage via backend API
      const response = await api.post("/billing/track-usage", {
        userId,
        emailCount,
        planKey,
      });

      const { emailsUsed, emailQuota, overage } = response.data;

      console.log(
        `📊 Usage: ${emailsUsed}/${emailQuota} (Overage: ${overage})`
      );

      // Auto-charge if:
      // 1. User is on an auto-billing plan
      // 2. They have overage
      // 3. Auto-charge is enabled for this call
      const needsCharging = isAutoBillingPlan && overage > 0 && autoCharge;

      if (needsCharging) {
        console.log(`💳 Auto-charging for ${overage} overage emails...`);
        const charged = await this.processAutoOverage(userId, overage);

        return {
          needsCharging: false, // Already handled
          currentCount: emailsUsed,
          charged,
        };
      }

      return {
        needsCharging: false,
        currentCount: emailsUsed,
      };
    } catch (error) {
      console.error("❌ Error tracking email usage:", error);
      logError("Error tracking email usage", { error, userId, emailCount });

      // Return safe defaults on error
      return {
        needsCharging: false,
        currentCount: 0,
      };
    }
  }

  /**
   * Charge user for additional email quota
   * @param userId User ID to charge
   * @param emailQuantity Number of emails to purchase (default 10k)
   */
  async chargeForAdditionalQuota(
    userId: string,
    emailQuantity: number = 10000
  ): Promise<boolean> {
    try {
      console.log(
        `💳 Stripe Service: Charging user ${userId} for ${emailQuantity} additional emails`
      );

      if (isDev) {
        // In development, simulate charge
        console.log(
          `[DEV] Would charge user ${userId} for ${emailQuantity} emails`
        );
        return true;
      }

      // Get user's plan to determine overage rate
      const subscription = await this.getSubscription(userId);
      const planKey = subscription?.plan || "usage";
      const plan = STRIPE_PLANS[planKey];

      if (!plan) {
        throw new Error(`Invalid plan: ${planKey}`);
      }

      // Calculate cost based on plan's overage rate
      const overageRate = plan.overage || 15; // Default $15 per 10k
      const cost = Math.ceil(emailQuantity / 10000) * overageRate;

      console.log(
        `💰 Charging $${cost} for ${emailQuantity} emails at $${overageRate}/10k rate`
      );

      // Charge via Stripe API
      const response = await api.post("/billing/charge-overage", {
        userId,
        emailQuantity,
        cost,
        planKey,
      });

      if (response.data.success) {
        console.log(`✅ Successfully charged for ${emailQuantity} emails`);
        logInfo("Email overage charge successful", {
          userId,
          emailQuantity,
          cost,
        });
        return true;
      } else {
        console.error("❌ Charge failed:", response.data.error);
        return false;
      }
    } catch (error) {
      console.error("❌ Error charging for additional quota:", error);
      logError("Error charging for additional quota", {
        error,
        userId,
        emailQuantity,
      });
      return false;
    }
  }

  /**
   * Check if user should be auto-charged for overage
   * @param userId User ID to check
   */
  async shouldAutoCharge(userId: string): Promise<boolean> {
    try {
      const subscription = await this.getSubscription(userId);
      const planKey = subscription?.plan || "usage";

      // Only auto-charge for specific plans
      return AUTO_BILLING_PLANS.includes(planKey);
    } catch (error) {
      console.error("Error checking auto-charge eligibility:", error);
      return false;
    }
  }

  /**
   * Process automatic overage charges
   * @param userId User ID to charge
   * @param overage Number of overage emails
   */
  async processAutoOverage(userId: string, overage: number): Promise<boolean> {
    try {
      // Round up to nearest 10k for charging
      const chargeQuantity = Math.ceil(overage / 10000) * 10000;

      console.log(
        `⚡ Processing auto-overage charge for ${chargeQuantity} emails (actual overage: ${overage})`
      );

      return await this.chargeForAdditionalQuota(userId, chargeQuantity);
    } catch (error) {
      console.error("Error processing auto-overage:", error);
      logError("Error processing auto-overage", { error, userId, overage });
      return false;
    }
  }

  /**
   * Calculate pricing for custom email volumes
   * @param emailVolume Number of emails per month
   */
  calculateCustomPlan(emailVolume: number) {
    // Base calculation: $10 per 10k emails
    const baseRate = 10; // $10 per 10k
    const baseCost = (emailVolume / 10000) * baseRate;

    // Apply volume discounts
    let discount = 0;
    if (emailVolume >= 10000000) discount = 0.5; // 50% off for 10M+
    else if (emailVolume >= 5000000) discount = 0.4; // 40% off for 5M+
    else if (emailVolume >= 2000000) discount = 0.3; // 30% off for 2M+
    else if (emailVolume >= 1000000) discount = 0.2; // 20% off for 1M+
    else if (emailVolume >= 500000) discount = 0.1; // 10% off for 500k+

    const finalPrice = Math.round(baseCost * (1 - discount));

    // Determine features based on volume
    let features = STRIPE_PLANS.usage.features;
    if (emailVolume >= 10000000) {
      // Ultimate tier features
      features = STRIPE_PLANS.ultimate.features;
    } else if (emailVolume >= 5000000) {
      // Elite tier features
      features = STRIPE_PLANS.elite.features;
    } else if (emailVolume >= 2000000) {
      // Professional tier features
      features = STRIPE_PLANS.professional.features;
    } else if (emailVolume >= 1000000) {
      // Scale tier features
      features = STRIPE_PLANS.scale.features;
    } else if (emailVolume >= 500000) {
      // Enterprise tier features
      features = STRIPE_PLANS.enterprise.features;
    } else if (emailVolume >= 200000) {
      // Pro tier features
      features = STRIPE_PLANS.pro.features;
    } else if (emailVolume >= 100000) {
      // Standard tier features
      features = STRIPE_PLANS.standard.features;
    } else if (emailVolume >= 40000) {
      // Basic tier features
      features = STRIPE_PLANS.basic.features;
    } else if (emailVolume >= 15000) {
      // Junior Pro tier features
      features = STRIPE_PLANS.junior_pro.features;
    }

    return {
      name: `Custom ${emailVolume.toLocaleString()} Plan`,
      price: finalPrice,
      emailQuota: emailVolume,
      overage: 10, // $10 per 10k overage for all custom plans
      features,
      description: `Custom plan for ${emailVolume.toLocaleString()} emails/month`,
      discount: discount * 100,
      isCustom: true,
    };
  }

  /**
   * Cancel user's subscription
   * @param userId User ID
   */
  async cancelSubscription(userId: string): Promise<boolean> {
    try {
      console.log(`🚫 Stripe Service: Canceling subscription for user ${userId}`);

      if (isDev) {
        console.log(`[DEV] Would cancel subscription for user ${userId}`);
        return true;
      }

      const response = await api.post(`/billing/cancel-subscription`, { userId });
      return response.data.success;
    } catch (error) {
      console.error("❌ Error canceling subscription:", error);
      logError("Error canceling subscription", { error, userId });
      return false;
    }
  }

  /**
   * Get user's current usage data
   * @param userId User ID
   */
  async getUserUsage(userId: string) {
    try {
      const response = await api.get(`/billing/usage/${userId}`);
      return response.data;
    } catch (error) {
      console.error("Error getting user usage:", error);
      return null;
    }
  }
}

export const stripeService = new StripeService();